export type Video = { titulo: string; url: string; descripcion: string; tags: string[]; fecha: string };
export type Embed = { kind: 'iframe' | 'video' | 'link'; src: string };

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;

/** Cómo mostrar cada URL: iframe (YouTube, Drive, SharePoint embed), <video> (mp4/webm) o solo el enlace. */
export function embedOf(url: string): Embed {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return { kind: 'link', src: url };
  }
  if (u.protocol !== 'https:') return { kind: 'link', src: url };
  const host = u.hostname.replace(/^www\.|^m\./, '');
  let youtubeId: string | null | undefined;
  if (host === 'youtu.be') youtubeId = u.pathname.slice(1);
  else if (host === 'youtube.com') youtubeId = u.searchParams.get('v') ?? u.pathname.match(/^\/(?:embed|shorts)\/([^/]+)/)?.[1];
  if (youtubeId && YOUTUBE_ID.test(youtubeId)) return { kind: 'iframe', src: `https://www.youtube-nocookie.com/embed/${youtubeId}` };
  const driveId = host === 'drive.google.com' ? u.pathname.match(/^\/file\/d\/([^/]+)/)?.[1] : undefined;
  if (driveId) return { kind: 'iframe', src: `https://drive.google.com/file/d/${driveId}/preview` };
  if (host.endsWith('.sharepoint.com') && /\/embed\.aspx$/i.test(u.pathname)) return { kind: 'iframe', src: u.toString() };
  if (/\.(mp4|webm)$/i.test(u.pathname)) return { kind: 'video', src: u.toString() };
  return { kind: 'link', src: u.toString() };
}

/** Entradas completas del YAML, de la más reciente a la más antigua. */
export function validVideos(raw: unknown): Video[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      (v): v is Video =>
        typeof v?.titulo === 'string' && typeof v?.url === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(String(v?.fecha ?? '')),
    )
    .map(v => ({ ...v, fecha: String(v.fecha), descripcion: v.descripcion ?? '', tags: Array.isArray(v.tags) ? v.tags.map(String) : [] }))
    .sort((a, b) => b.fecha.localeCompare(a.fecha));
}

/** Búsqueda (título, descripción y tags, sin tildes ni mayúsculas) y filtro por tag. */
export function filterVideos(videos: Video[], query: string, tag?: string): Video[] {
  const norm = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const words = norm(query).split(/\s+/).filter(Boolean);
  return videos.filter(v => {
    if (tag && !v.tags.includes(tag)) return false;
    const text = norm(`${v.titulo} ${v.descripcion} ${v.tags.join(' ')}`);
    return words.every(w => text.includes(w));
  });
}
